import { useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ProgressContext } from "../ContextAPI";
import { Box, Typography, TextField, InputAdornment } from "@mui/material";
import { api } from "../../../utils/api";
import { companyId } from "../../../utils/companyId";
import ActionButtons from "../InitialKickoff/MaterialTopics/ActionButtons";
import axios from "axios";
import { percentageError, numberError } from "../../../utils/validationMessage";
import SkipQuestionare from "../../Common/SkipQuestionare";
import InitialQuestionareSidebar from "../../../Layout/InitialQuestionareSidebar";
import { getQuestionareSubmitted } from "../../../utils/storage";

const EmployeeDevelopment = () => {
  const { setUpdate } = useContext(ProgressContext);
  const navigate = useNavigate();
  const companyDetails = getQuestionareSubmitted();

  const [formData, setFormData] = useState({
    employeeAtStartofYear: "",
    employeeAtEndofYear: "",
    employeeLeftDuringYear: "",
    turnoverRate: "",
    trainingHours: "",
    healthAndSafety: "",
    numberOfInjuries: "",
    totalWorkingHours: "",
  });
  const [errors, setErrors] = useState({});
  const [isExist, setIsExist] = useState(false);

  // Memoized fetch function
  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(
        `${api}/social/employee_well_being_development/${companyId}`
      );
      if (response.data) {
        setFormData({
          employeeAtStartofYear: response.data.employeeAtStartofYear || "",
          employeeAtEndofYear: response.data.employeeAtEndofYear || "",
          employeeLeftDuringYear: response.data.employeeLeftDuringYear || "",
          turnoverRate: response.data.turnoverRate?.value || "",
          trainingHours: response.data.trainingHoursPerEmployee?.value || "",
          healthAndSafety: response.data.employeeHealthAndSafety?.value || "",
          numberOfInjuries: response.data.numberOfInjuries || "",
          totalWorkingHours: response.data.totalWorkingHours || "",
        });
      }
      setIsExist(true);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, []);

  useEffect(() => {
    setUpdate("SOCIAL");
    fetchData();
  }, [setUpdate, fetchData]);

  useEffect(() => {
    const calculateTurnoverRate = () => {
      const start = Number(formData.employeeAtStartofYear);
      const end = Number(formData.employeeAtEndofYear);
      const left = Number(formData.employeeLeftDuringYear);

      if (start > 0 && (left > 0 || end !== start)) {
        const avgEmployees = (start + end) / 2;
        const turnover = ((left || start - end) / avgEmployees) * 100;
        setFormData((prev) => ({
          ...prev,
          turnoverRate: turnover.toFixed(2),
        }));
      } else {
        setFormData((prev) => ({ ...prev, turnoverRate: "" }));
      }
    };

    calculateTurnoverRate();
  }, [
    formData.employeeAtStartofYear,
    formData.employeeAtEndofYear,
    formData.employeeLeftDuringYear,
  ]);

  // Validation function
  const validate = useCallback(() => {
    const newErrors = {};
    const {
      employeeAtStartofYear,
      employeeAtEndofYear,
      employeeLeftDuringYear,
      turnoverRate,
      trainingHours,
      healthAndSafety,
      numberOfInjuries,
      totalWorkingHours,
    } = formData;

    if (isNaN(employeeAtStartofYear))
      newErrors.employeeAtStartofYear = "Invalid number";
    else if (employeeAtStartofYear < 0)
      newErrors.employeeAtStartofYear = numberError;

    if (isNaN(employeeAtEndofYear))
      newErrors.employeeAtEndofYear = "Invalid number";
    else if (employeeAtEndofYear < 0)
      newErrors.employeeAtEndofYear = numberError;

    if (isNaN(employeeLeftDuringYear))
      newErrors.employeeLeftDuringYear = "Invalid number";

    if (isNaN(turnoverRate)) newErrors.turnoverRate = "Invalid number";
    else if (turnoverRate < 0 || turnoverRate > 100)
      newErrors.turnoverRate = percentageError;

    if (isNaN(trainingHours)) newErrors.trainingHours = "Invalid number";
    else if (trainingHours < 0) newErrors.trainingHours = numberError;

    if (isNaN(healthAndSafety)) newErrors.healthAndSafety = "Invalid number";
    else if (healthAndSafety < 0 || healthAndSafety > 100)
      newErrors.healthAndSafety = percentageError;

    if (isNaN(numberOfInjuries)) newErrors.numberOfInjuries = "Invalid number";
    else if (numberOfInjuries < 0) newErrors.numberOfInjuries = numberError;

    if (isNaN(totalWorkingHours))
      newErrors.totalWorkingHours = "Invalid number";
    else if (totalWorkingHours < 0) newErrors.totalWorkingHours = numberError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Save function
  const Save = useCallback(async () => {
    if (!validate()) return;

    const payload = {
      employeeAtStartofYear: formData.employeeAtStartofYear,
      employeeAtEndofYear: formData.employeeAtEndofYear,
      employeeLeftDuringYear: formData.employeeLeftDuringYear,
      numberOfInjuries: formData.numberOfInjuries,
      totalWorkingHours: formData.totalWorkingHours,
      turnoverRate: { value: formData.turnoverRate, unit: "%" },
      trainingHoursPerEmployee: {
        value: formData.trainingHours,
        unit: "hours",
      },
      employeeHealthAndSafety: {
        value: formData.healthAndSafety,
        metric: "lost time frequency injury rate",
      },
      companyId,
      employee_development: Boolean(
        formData.turnoverRate &&
          formData.trainingHours &&
          formData.healthAndSafety
      ),
    };

    try {
      const endpoint = isExist
        ? `${api}/social/employee_well_being_development/${companyId}`
        : `${api}/social/employee_well_being_development`;

      const method = isExist ? axios.put : axios.post;
      const response = await method(endpoint, payload);

      if ([200, 201].includes(response.status)) {
        navigate(
          companyDetails?.questionareSubmitted
            ? "/preview"
            : "/social/diversity"
        );
      }
    } catch (error) {
      console.error("Error saving data:", error);
    }
  }, [formData, validate, isExist, navigate, companyDetails]);

  const Back = useCallback(() => {
    setUpdate("ESG");
    navigate("/environment/sustainable-sourcing");
  }, [setUpdate, navigate]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // Common TextField props
  const commonTextFieldProps = (
    field,
    label,
    unit,
    error,
    disabled = false
  ) => ({
    variant: "outlined",
    value: formData[field],
    disabled: disabled,
    onChange: (e) => {
      const value = e.target.value;
      if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
        handleChange(field)(e);
      }
    },
    onKeyDown: (e) => {
      if (
        !/[0-9.]/.test(e.key) &&
        !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
          e.key
        )
      ) {
        e.preventDefault();
      }
    },
    sx: {
      mb: 1,
      width: "100%",
      backgroundColor: "#EDEDED",
      borderRadius: "8px",
      "& .MuiOutlinedInput-root": {
        "&.Mui-focused fieldset": {
          borderColor: error ? "#D34040" : "#1976d2",
        },
      },
      "& fieldset": {
        borderColor: error ? "#D34040" : "inherit",
      },
    },
    placeholder: "Please enter number",
    error: Boolean(error),
    helperText: error && (
      <span style={{ color: "#D34040", marginLeft: "-12px", fontSize: "14px" }}>
        {error}
      </span>
    ),
    InputProps: {
      endAdornment: unit ? (
        <InputAdornment position="end">
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                height: "44px",
                width: "1px",
                bgcolor: "#6b6c6d",
                marginRight: "8px",
              }}
            />
            {unit}
          </Box>
        </InputAdornment>
      ) : null,
    },
  });

  return (
    <Box sx={{ display: "flex", backgroundColor: "#EDEDED", height: "100vh" }}>
      <Box sx={{ width: "25vw" }}>
        <InitialQuestionareSidebar />
      </Box>
      <Box
        sx={{
          p: 2,
          mt: 3,
          width: "75vw",
          height: "100vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "80%",
            mb: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              width: "60%",
              fontWeight: "bold",
              zIndex: 1,
              py: 1,
              borderRadius: 1,
              alignSelf: "flex-start",
            }}
          >
            SOCIAL: EMPLOYEE WELL BEING & DEVELOPMENT
          </Typography>
          <SkipQuestionare />
        </Box>

        <Box sx={{ width: "80%" }}>
          {[
            {
              field: "employeeAtStartofYear",
              label: "Employees at the Company at the Start of the Year",
              error: errors.employeeAtStartofYear,
            },
            {
              field: "employeeAtEndofYear",
              label: "Employees at the Company at the End of the Year",
              error: errors.employeeAtEndofYear,
            },
            {
              field: "employeeLeftDuringYear",
              label: "Employees who Left During the Year",
              error: errors.employeeLeftDuringYear,
            },
            {
              field: "turnoverRate",
              label: "Turnover Rate",
              unit: "%",
              error: errors.turnoverRate,
              disabled: true,
            },
            {
              field: "trainingHours",
              label: "Training Hours per Employee",
              unit: "hours",
              error: errors.trainingHours,
            },
            {
              field: "totalWorkingHours",
              label: "Total working hours in the year",
              error: errors.totalWorkingHours,
            },
            {
              field: "numberOfInjuries",
              label: "Number of lost time injuries (LTI) during the year",
              error: errors.numberOfInjuries,
            },
            {
              field: "healthAndSafety",
              label: "Lost time injury frequency rate (LTIFR)",
              // unit: "lost time frequency injury rate",
              error: errors.healthAndSafety,
            },
          ].map(({ field, label, unit, error, disabled }) => (
            <Box key={field}>
              <Typography
                variant="body1"
                sx={{ mb: 1, mt: 2, fontWeight: "bold" }}
              >
                {label}
              </Typography>
              <TextField
                {...commonTextFieldProps(field, label, unit, error, disabled)}
              />
            </Box>
          ))}

          <ActionButtons
            Back={Back}
            Move={Save}
            disable={companyDetails?.questionareSubmitted}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default EmployeeDevelopment;
